import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert } from "@mui/material";
import { Repeat as RepeatIcon } from "@mui/icons-material";

interface RecurringEventScopeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (scope: "instance" | "future" | "series") => void;
  eventTitle: string;
  action: "move" | "resize" | "delete" | "edit";
  loading?: boolean;
}

export const RecurringEventScopeModal: React.FC<RecurringEventScopeModalProps> = ({ open, onClose, onConfirm, eventTitle, action, loading = false }) => {
  const getActionText = () => {
    switch (action) {
      case "move":
        return "move";
      case "resize":
        return "resize";
      case "delete":
        return "delete";
      case "edit":
        return "update";
      default:
        return "modify";
    }
  };

  const getActionPastTense = () => {
    switch (action) {
      case "move":
        return "moved";
      case "resize":
        return "resized";
      case "delete":
        return "deleted";
      case "edit":
        return "updated";
      default:
        return "modified";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <RepeatIcon />
        Recurring Event - Choose Scope
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a recurring event. Choose which occurrences to {getActionText()}:
        </Alert>

        <Typography variant="h6" gutterBottom>
          &ldquo;{eventTitle}&rdquo;
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Button variant="outlined" onClick={() => onConfirm("instance")} disabled={loading} sx={{ justifyContent: "flex-start", textAlign: "left" }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                This Event Only
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getActionPastTense()} only this specific occurrence
              </Typography>
            </Box>
          </Button>

          <Button variant="outlined" onClick={() => onConfirm("future")} disabled={loading} sx={{ justifyContent: "flex-start", textAlign: "left" }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                This and Future Events
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getActionPastTense()} this occurrence and all future occurrences
              </Typography>
            </Box>
          </Button>

          <Button variant="outlined" onClick={() => onConfirm("series")} disabled={loading} sx={{ justifyContent: "flex-start", textAlign: "left" }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                All Events in Series
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getActionPastTense()} all occurrences (past and future)
              </Typography>
            </Box>
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
